import { supabase } from '@/utils/supabase';
import type {
  PickupActionRequest,
  PickupCacheSchedule,
  PickupOpportunity,
  PickupOpportunityAttempt,
  PickupOpportunityEvent,
  PickupOpenOpportunityBadge,
  PickupPipelineData,
  PickupPipelineUser
} from '@/types/pickupOpportunity';
import type {
  PickupAnalysisOpportunityAttempt,
  PickupEligibleProduct,
  PricingWaterfall
} from '@/components/reports/types';

const PAGE_SIZE = 1000;
const ID_CHUNK_SIZE = 200;
const OPPORTUNITY_ID_CHUNK_SIZE = 100;

interface FunctionErrorPayload {
  error?: string;
  message?: string;
}

interface PickupActionResponse {
  success: boolean;
  result?: Record<string, unknown>;
  error?: string;
  message?: string;
}

export interface PickupOpportunityPricingEvidence {
  id: string;
  ob_search_id: string | null;
  pricing_waterfall: PricingWaterfall | null;
  eligible_products: PickupEligibleProduct[] | null;
}

function chunks<T>(values: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    result.push(values.slice(index, index + size));
  }
  return result;
}

async function functionErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (!error || typeof error !== 'object' || !('context' in error)) {
    return error instanceof Error ? error.message : fallback;
  }
  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) {
    return error instanceof Error ? error.message : fallback;
  }
  try {
    const payload = await context.clone().json() as FunctionErrorPayload;
    return payload.message || payload.error || fallback;
  } catch {
    return fallback;
  }
}

async function fetchOpportunities(): Promise<PickupOpportunity[]> {
  const rows: PickupOpportunity[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('pickup_opportunities')
      .select('*')
      .order('latest_lock_expiration_at', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      throw new Error(`Failed to load Pick Up opportunities: ${error.message}`);
    }
    const page = (data || []) as unknown as PickupOpportunity[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) {
      break;
    }
  }
  return rows;
}

async function fetchAttempts(): Promise<PickupOpportunityAttempt[]> {
  const rows: PickupOpportunityAttempt[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('pickup_opportunity_attempts')
      .select('*')
      .order('opportunity_id', { ascending: true })
      .order('rank', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      throw new Error(`Failed to load Pick Up investor ladder: ${error.message}`);
    }
    const page = (data || []) as unknown as PickupOpportunityAttempt[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) {
      break;
    }
  }
  return rows;
}

async function fetchOpenOpportunityRows(): Promise<Array<Pick<
  PickupOpportunity,
  'id' | 'ob_loan_id' | 'active_attempt_id'
>>> {
  const rows: Array<Pick<PickupOpportunity, 'id' | 'ob_loan_id' | 'active_attempt_id'>> = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('pickup_opportunities')
      .select('id, ob_loan_id, active_attempt_id')
      .eq('status', 'open')
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      throw new Error(`Failed to load open Pick Up opportunities: ${error.message}`);
    }
    const page = (data || []) as unknown as Array<Pick<
      PickupOpportunity,
      'id' | 'ob_loan_id' | 'active_attempt_id'
    >>;
    rows.push(...page);
    if (page.length < PAGE_SIZE) {
      break;
    }
  }
  return rows;
}

async function fetchAttemptsForOpportunities(
  opportunityIds: string[]
): Promise<PickupOpportunityAttempt[]> {
  const rows: PickupOpportunityAttempt[] = [];
  for (const ids of chunks(opportunityIds, OPPORTUNITY_ID_CHUNK_SIZE)) {
    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await supabase
        .from('pickup_opportunity_attempts')
        .select('*')
        .in('opportunity_id', ids)
        .order('opportunity_id', { ascending: true })
        .order('rank', { ascending: true })
        .order('id', { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) {
        throw new Error(`Failed to load open Pick Up candidate ladders: ${error.message}`);
      }
      const page = (data || []) as unknown as PickupOpportunityAttempt[];
      rows.push(...page);
      if (page.length < PAGE_SIZE) {
        break;
      }
    }
  }
  return rows;
}

async function fetchUsers(): Promise<PickupPipelineUser[]> {
  const rows: PickupPipelineUser[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('users')
      .select('id, full_name, first_name, last_name, email')
      .eq('employment_status', 'active')
      .is('deleted_at', null)
      .order('full_name', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      throw new Error(`Failed to load Pick Up owners: ${error.message}`);
    }
    const page = (data || []) as unknown as PickupPipelineUser[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) {
      break;
    }
  }
  return rows;
}

async function fetchCacheSchedules(cacheIds: string[]): Promise<PickupCacheSchedule[]> {
  const rows: PickupCacheSchedule[] = [];
  for (const ids of chunks(Array.from(new Set(cacheIds.filter(Boolean))), ID_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('ob_loan_cache')
      .select([
        'id',
        'pickup_next_pricing_eligible_at',
        'pickup_force_reprice_next_allowed_at',
        'pickup_verification_status',
        'pickup_verification_mismatch_fields',
        'pickup_verification_last_error',
        'pickup_verification_retry_after',
        'pickup_last_verified_at',
        'pickup_verified_lock_duration_days',
        'pickup_verified_ob_lock_period_bucket'
      ].join(', '))
      .in('id', ids)
      .order('id', { ascending: true });
    if (error) {
      throw new Error(`Failed to load Pick Up pricing schedule: ${error.message}`);
    }
    rows.push(...((data || []) as unknown as PickupCacheSchedule[]));
  }
  return rows;
}

export async function fetchPickupPipelineData(): Promise<PickupPipelineData> {
  const [opportunities, attempts, users] = await Promise.all([
    fetchOpportunities(),
    fetchAttempts(),
    fetchUsers()
  ]);
  const cacheSchedules = await fetchCacheSchedules(
    opportunities.map((opportunity) => opportunity.ob_loan_cache_id).filter(
      (id): id is string => Boolean(id)
    )
  );
  return { opportunities, attempts, users, cacheSchedules };
}

export async function fetchPickupOpportunityPricingEvidence(
  resultId: string | null
): Promise<PickupOpportunityPricingEvidence | null> {
  if (!resultId) {
    return null;
  }
  const { data, error } = await supabase
    .from('ob_pickup_results')
    .select('id, ob_search_id, pricing_waterfall, eligible_products')
    .eq('id', resultId)
    .maybeSingle();
  if (error) {
    throw new Error(`Failed to load Pick Up price evidence: ${error.message}`);
  }
  return data as unknown as PickupOpportunityPricingEvidence | null;
}

export async function fetchOpenPickupOpportunityBadges(): Promise<PickupOpenOpportunityBadge[]> {
  const opportunities = await fetchOpenOpportunityRows();
  if (opportunities.length === 0) {
    return [];
  }
  const attempts = await fetchAttemptsForOpportunities(
    opportunities.map((opportunity) => opportunity.id)
  );
  const attemptsByOpportunity = new Map<string, PickupOpportunityAttempt[]>();
  for (const attempt of attempts) {
    const ladder = attemptsByOpportunity.get(attempt.opportunity_id) || [];
    ladder.push(attempt);
    attemptsByOpportunity.set(attempt.opportunity_id, ladder);
  }

  return opportunities.flatMap((opportunity) => {
    const ladder = attemptsByOpportunity.get(opportunity.id) || [];
    const activeAttempt = ladder.find((attempt) => attempt.id === opportunity.active_attempt_id)
      || ladder.find((attempt) => attempt.is_active);
    if (!activeAttempt) {
      return [];
    }
    return [{
      opportunityId: opportunity.id,
      obLoanId: opportunity.ob_loan_id,
      attemptStatus: activeAttempt.status,
      investorName: activeAttempt.investor_name,
      candidateRank: activeAttempt.rank,
      candidateCount: ladder.length
    }];
  });
}

export async function fetchOpenPickupOpportunityAttemptsForLoans(
  obLoanIds: number[]
): Promise<PickupAnalysisOpportunityAttempt[]> {
  const opportunities: Array<Pick<PickupOpportunity, 'id' | 'ob_loan_id'>> = [];
  const uniqueObLoanIds = Array.from(new Set(
    obLoanIds.filter((id) => Number.isInteger(id) && id > 0)
  ));
  for (const ids of chunks(uniqueObLoanIds, OPPORTUNITY_ID_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('pickup_opportunities')
      .select('id, ob_loan_id')
      .eq('status', 'open')
      .in('ob_loan_id', ids)
      .order('id', { ascending: true });
    if (error) {
      throw new Error(`Failed to load displayed Pick Up opportunities: ${error.message}`);
    }
    opportunities.push(...((data || []) as unknown as Array<Pick<
      PickupOpportunity,
      'id' | 'ob_loan_id'
    >>));
  }
  if (opportunities.length === 0) {
    return [];
  }

  const obLoanIdByOpportunity = new Map(
    opportunities.map((opportunity) => [opportunity.id, opportunity.ob_loan_id])
  );
  const attempts = await fetchAttemptsForOpportunities(
    opportunities.map((opportunity) => opportunity.id)
  );
  return attempts.flatMap((attempt) => {
    const obLoanId = obLoanIdByOpportunity.get(attempt.opportunity_id);
    return obLoanId === undefined ? [] : [{
      id: attempt.id,
      opportunityId: attempt.opportunity_id,
      obLoanId,
      investorName: attempt.investor_name,
      status: attempt.status
    }];
  });
}

export async function fetchPickupOpportunityEvents(
  opportunityId: string
): Promise<PickupOpportunityEvent[]> {
  const rows: PickupOpportunityEvent[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('pickup_opportunity_events')
      .select('*')
      .eq('opportunity_id', opportunityId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      throw new Error(`Failed to load Pick Up audit timeline: ${error.message}`);
    }
    const page = (data || []) as unknown as PickupOpportunityEvent[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) {
      break;
    }
  }
  return rows;
}

export async function applyPickupOpportunityAction(
  request: PickupActionRequest
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke<PickupActionResponse>(
    'pickup-opportunity-action',
    {
      body: {
        action: 'applyAction',
        opportunityId: request.opportunityId,
        attemptId: request.attemptId,
        opportunityAction: request.action,
        expectedStatus: request.expectedStatus,
        payload: request.payload
      }
    }
  );
  if (error || data?.success !== true) {
    throw new Error(await functionErrorMessage(error, data?.message || 'Pick Up disposition failed.'));
  }
  return data.result || {};
}

export async function uploadPickupLockConfirmation(
  opportunityId: string,
  attemptId: string,
  file: File
): Promise<void> {
  const body = new FormData();
  body.set('action', 'uploadLockConfirmation');
  body.set('opportunityId', opportunityId);
  body.set('attemptId', attemptId);
  body.set('file', file);
  const { data, error } = await supabase.functions.invoke<PickupActionResponse>(
    'pickup-opportunity-action',
    { body }
  );
  if (error || data?.success !== true) {
    throw new Error(await functionErrorMessage(error, data?.message || 'Lock confirmation upload failed.'));
  }
}

export async function getPickupLockConfirmationUrl(
  opportunityId: string,
  attemptId: string
): Promise<string> {
  const { data, error } = await supabase.functions.invoke<PickupActionResponse & { url?: string }>(
    'pickup-opportunity-action',
    {
      body: {
        action: 'getLockConfirmationUrl',
        opportunityId,
        attemptId
      }
    }
  );
  if (error || data?.success !== true || !data.url) {
    throw new Error(await functionErrorMessage(error, data?.message || 'Could not open the lock confirmation.'));
  }
  return data.url;
}
